import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
import classList from "flarum/common/utils/classList";
import ChatMessage from "./ChatMessage";
import ChatComposer from "./ChatComposer";
import ChatSelectionBar from "./ChatSelectionBar";
import { MessageStreamSkeleton } from "./Skeletons";
import { channelIcon } from "../utils/channelIcon";
import { channelActions, openChannelInfo } from "../utils/channelActions";
import { jumpToMessage } from "../utils/jumpToMessage";
import { messagePreview } from "../../common/utils/preview";
import iconLabel from "../utils/iconLabel";
/**
 * A channel's header, message stream and composer.
 *
 * The stream is bottom-anchored: it stays pinned to the newest message while the
 * user is at the bottom, and holds its scroll position when they are not. Getting
 * this wrong is the single most noticeable chat bug, so the anchoring logic is
 * kept explicit rather than left to the browser.
 */
export default class ChannelView extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "scroller", null);
    /** Whether the user is at (or near) the bottom of the stream. */
    _defineProperty(this, "pinned", true);
    /** Scroll height before a prepend, used to restore position after paging up. */
    _defineProperty(this, "heightBeforePrepend", null);
    _defineProperty(this, "lastRenderedCount", 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.load();
  }
  onbeforeupdate(vnode) {
    var _this$attrs;
    // `this.attrs` still holds the previous attrs at this point; Component
    // assigns the incoming vnode's attrs in super.onbeforeupdate().
    const previousId = (_this$attrs = this.attrs) == null || (_this$attrs = _this$attrs.channel) == null ? void 0 : _this$attrs.id();
    super.onbeforeupdate(vnode);

    // Switching channels inside the same component instance: reset and reload.
    if (previousId !== undefined && vnode.attrs.channel.id() !== previousId) {
      this.pinned = true;
      this.lastRenderedCount = 0;
      this.load();
    }
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.scroller = vnode.dom.querySelector(".ChatChannel-stream");
    this.scrollToBottom();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    const stream = this.attrs.state.stream(Number(this.attrs.channel.id()));
    const count = stream.messages.length;

    // Restore the pre-prepend position after paging upwards, so the viewport does
    // not jump to the top when older messages arrive.
    if (this.heightBeforePrepend !== null && this.scroller) {
      this.scroller.scrollTop = this.scroller.scrollHeight - this.heightBeforePrepend;
      this.heightBeforePrepend = null;
    } else if (count > this.lastRenderedCount && this.pinned) {
      this.scrollToBottom();
    }
    this.lastRenderedCount = count;
  }
  view() {
    const _this$attrs2 = this.attrs,
      channel = _this$attrs2.channel,
      state = _this$attrs2.state;
    const stream = state.stream(Number(channel.id()));
    return m("div", {
      className: "ChatChannel"
    }, this.header(), this.pinnedBar(), m("div", {
      className: "ChatChannel-streamWrap"
    }, m("div", {
      className: "ChatChannel-stream",
      onscroll: e => this.onScroll(e)
    }, stream.loading && stream.messages.length === 0 ? this.skeleton() : null, stream.hasMore && stream.messages.length > 0 ? m("div", {
      className: "ChatChannel-loadMore"
    }, stream.loading ? MessageStreamSkeleton(2) : null) : null, stream.loadedInitial && stream.messages.length === 0 ? m("div", {
      className: "ChatBrowse-empty"
    }, app.translator.trans("ramon-chat.forum.channel.no_messages")) : null, this.rows(stream.messages, stream.dividerAfterId)), this.scrollDownButton()), this.typingIndicator(), state.selecting ? m(ChatSelectionBar, {
      channel: channel,
      state: state
    }) : m(ChatComposer, {
      channel: channel,
      state: state,
      onSent: () => this.onSent()
    }), m("div", {
      className: "ChatChannel-liveRegion",
      role: "status",
      "aria-live": "polite"
    }));
  }

  /**
   * The pinned message, as a strip under the header — the WhatsApp arrangement.
   *
   * Shown in the drawer as well as the full-screen page: the drawer has no room for
   * the pinned panel, and a pin nobody can see is pointless. Clicking it jumps to
   * the message when it is in the loaded window, which is the behaviour the strip
   * implies; when it is not loaded the strip stays a label rather than pretending
   * to navigate somewhere.
   */
  pinnedBar() {
    const _this$attrs3 = this.attrs,
      channel = _this$attrs3.channel,
      state = _this$attrs3.state;
    const pinned = state.latestPinned(Number(channel.id()));
    if (!pinned) return null;
    const text = messagePreview(pinned);
    const reachable = state.stream(Number(channel.id())).messages.some(message => message.id() === pinned.id());
    const count = state.pinnedCount(Number(channel.id()));
    return m("div", {
      className: "ChatChannel-pinnedBar"
    }, m("button", {
      type: "button",
      className: "ChatChannel-pinnedBar-jump",
      title: app.translator.trans("ramon-chat.forum.channel.pinned_messages", {}, true),
      onclick: () => this.jumpToPinned(pinned),
      disabled: !reachable
    }, m("i", {
      className: "ChatChannel-pinnedBar-icon fas fa-thumbtack",
      "aria-hidden": "true"
    }), m("span", {
      className: "ChatChannel-pinnedBar-text"
    }, text || app.translator.trans("ramon-chat.forum.message.pinned"))), count > 1 ? m("button", {
      type: "button",
      className: classList("ChatChannel-pinnedBar-all", {
        "ChatChannel-pinnedBar-all--active": state.showPinned
      }),
      title: app.translator.trans("ramon-chat.forum.channel.view_all_pinned", {
        count
      }, true),
      onclick: () => {
        state.togglePinned();
        m.redraw();
      }
    }, m("span", {
      className: "ChatChannel-pinnedBar-count"
    }, count), m("i", {
      className: "fas fa-angle-right",
      "aria-hidden": "true"
    })) : null);
  }
  jumpToPinned(pinned) {
    jumpToMessage(pinned.id(), this.scroller);
  }

  /**
   * The channel's own bar: mark, name, description, actions.
   *
   * Absent when embedded. The drawer is 320px of vertical space and drew two
   * bars stacked, both naming the same channel — its own, for the window
   * controls, and this one. It now carries both, so this would be the second
   * copy rather than the only one.
   */
  header() {
    const _this$attrs4 = this.attrs,
      channel = _this$attrs4.channel,
      state = _this$attrs4.state,
      onBack = _this$attrs4.onBack,
      embedded = _this$attrs4.embedded;
    if (embedded) return null;
    return m("div", {
      className: "ChatChannel-header"
    }, onBack ? m(Button, Object.assign({
      className: "Button Button--icon Button--flat",
      icon: "fas fa-chevron-left"
    }, iconLabel(app.translator.trans("ramon-chat.forum.sidebar.back_to_channels", {}, true)), {
      onclick: onBack
    })) : null, m("button", {
      type: "button",
      className: "ChatChannel-title",
      onclick: () => openChannelInfo(channel)
    }, channelIcon(channel, "ChatChannel-icon"), m("span", {
      className: "ChatChannel-titleText"
    }, m("span", {
      className: "ChatChannel-name"
    }, channel.displayName()), channel.description() ? m("span", {
      className: "ChatChannel-description"
    }, channel.description()) : null)), m("div", {
      className: "ChatChannel-headerActions"
    }, channelActions(channel, state).map(action => m(Button, Object.assign({
      key: action.key,
      className: classList("Button Button--icon Button--flat", {
        "ChatChannel-headerAction--active": action.active
      }),
      icon: action.icon
    }, iconLabel(action.label), {
      loading: action.loading,
      onclick: action.onclick
    })))));
  }

  /**
   * Interleaves date separators and the unread divider with the message rows.
   */
  rows(messages, dividerAfterId) {
    const out = [];
    let lastDate = null;
    let dividerPlaced = false;
    messages.forEach((message, index) => {
      const previous = index > 0 ? messages[index - 1] : null;
      const at = message.createdAt();
      const dateKey = at ? at.toDateString() : null;
      if (dateKey && dateKey !== lastDate) {
        out.push(m("div", {
          className: "ChatDateSeparator",
          key: "date-".concat(dateKey)
        }, m("span", {
          className: "ChatDateSeparator-label"
        }, this.dateLabel(at))));
        lastDate = dateKey;
      }
      if (!dividerPlaced && dividerAfterId !== null && Number(message.id()) > dividerAfterId) {
        out.push(m("div", {
          className: "ChatUnreadDivider",
          key: "unread"
        }, m("span", {
          className: "ChatUnreadDivider-label"
        }, app.translator.trans("ramon-chat.forum.stream.new_messages"))));
        dividerPlaced = true;
      }
      out.push(m(ChatMessage, {
        key: message.id(),
        message: message,
        previous: previous,
        state: this.attrs.state,
        onReply: msg => this.reply(msg),
        onEdit: msg => this.edit(msg),
        onOpenThread: msg => this.openThread(msg)
      }));
    });
    return out;
  }
  dateLabel(date) {
    const today = new Date();
    const yesterday = new Date(today.getTime() - 86400000);
    if (date.toDateString() === today.toDateString()) {
      return app.translator.trans("ramon-chat.forum.stream.today", {}, true);
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return app.translator.trans("ramon-chat.forum.stream.yesterday", {}, true);
    }

    // Through Day.js rather than toLocaleDateString: the browser's locale is
    // not the forum's, so a reader with an English forum on a Portuguese
    // machine saw "27 de agosto" between English messages. Core exposes the
    // dayjs instance the language pack has set the locale on, and the format
    // token is a translation so a pack can reorder it.
    return app.translator.formatDateTime(dayjs(date), "ramon-chat.forum.stream.date_format");
  }
  typingIndicator() {
    const names = this.attrs.state.typistsIn(Number(this.attrs.channel.id()));
    if (names.length === 0) return m("div", {
      className: "ChatTyping"
    });
    return m("div", {
      className: "ChatTyping"
    }, m("span", {
      className: "ChatTyping-dots"
    }, m("span", null), m("span", null), m("span", null)), m("span", null, names.length === 1 ? app.translator.trans("ramon-chat.forum.typing.one", {
      username: names[0]
    }) : app.translator.trans("ramon-chat.forum.typing.several", {
      count: names.length
    })));
  }
  skeleton() {
    return m("div", {
      className: "ChatSkeleton"
    }, [0, 1, 2, 3, 4].map(i => m("div", {
      className: "ChatSkeleton-row",
      key: i
    }, m("div", {
      className: "ChatSkeleton-avatar"
    }), m("div", {
      className: "ChatSkeleton-lines"
    }, m("div", {
      className: "ChatSkeleton-line ChatSkeleton-line--short"
    }), m("div", {
      className: "ChatSkeleton-line ChatSkeleton-line--long"
    })))));
  }

  // ── Behaviour ──────────────────────────────────────────────────────────────

  async load() {
    const _this$attrs5 = this.attrs,
      channel = _this$attrs5.channel,
      state = _this$attrs5.state;
    const channelId = Number(channel.id());

    // Not awaited: the pinned bar is secondary to the conversation, and blocking
    // the first page on it would delay every channel open by a round trip.
    state.loadPinnedPreview(channelId).catch(() => {});
    await state.loadChannel(channelId);

    // Mark read only after the first page is on screen, so the divider has
    // already been positioned from the pre-read marker.
    state.markRead(channelId);
  }
  onScroll(e) {
    const el = e.target;
    const wasPinned = this.pinned;

    // 40px of tolerance: "at the bottom" should survive sub-pixel rounding and a
    // partially visible last row.
    this.pinned = el.scrollHeight - el.scrollTop - el.clientHeight < 40;

    // Scroll fires continuously and Mithril redraws after every handler bound
    // this way, so a drag through the middle of a long stream repaints it dozens
    // of times over. Suppressed only while staying *away* from the bottom, which
    // is the one case with nothing to show for it: the button below is already
    // visible, `markRead` is not reached, and `fetchPage` redraws itself when its
    // request lands. Staying pinned still redraws — `markRead` mutates the unread
    // badge and has no redraw of its own.
    if (!this.pinned && !wasPinned) {
      e.redraw = false;
    }
    if (el.scrollTop < 120) {
      const stream = this.attrs.state.stream(Number(this.attrs.channel.id()));
      if (stream.hasMore && !stream.loading) {
        this.heightBeforePrepend = el.scrollHeight;
        this.attrs.state.fetchPage(Number(this.attrs.channel.id()));
      }
    }
    if (this.pinned) {
      this.attrs.state.markRead(Number(this.attrs.channel.id()));
    }
  }

  /**
   * The way back down, for when the stream has been left somewhere above the
   * newest message.
   *
   * Jumping to a pinned message is the case that makes this necessary: it can
   * land you hours up the conversation with no affordance but a long scroll, and
   * on a phone that is a lot of dragging. Same control WhatsApp draws in the same
   * corner, for the same reason.
   *
   * Tied to `pinned` rather than to a scroll-offset threshold of its own —
   * `pinned` is already what decides whether the stream follows new messages, so
   * the button is visible exactly when it is not following, and never lingers
   * over a stream that is already at the bottom.
   */
  scrollDownButton() {
    if (this.pinned) return null;
    const label = app.translator.trans("ramon-chat.forum.channel.scroll_to_latest", {}, true);
    return m("button", {
      type: "button",
      className: "ChatChannel-scrollDown",
      title: label,
      "aria-label": label,
      onclick: () => {
        this.pinned = true;
        this.scrollToBottom();
      }
    }, m("i", {
      className: "fas fa-chevron-down",
      "aria-hidden": "true"
    }));
  }

  /**
   * Jumps the stream to the newest message.
   *
   * Instant, including for the button — an animated version of this does not
   * survive its surroundings. `onupdate` re-anchors to the bottom the moment a
   * message arrives while pinned, which overrides an animation mid-flight; and a
   * smooth `scrollTo` resolves its target once, so rows still laying out below
   * (an image finishing, the reconcile filling a row in) leave it settling short
   * of the bottom — on whichever row happened to be there.
   */
  scrollToBottom() {
    if (!this.scroller) return;
    this.scroller.scrollTop = this.scroller.scrollHeight;
  }
  onSent() {
    this.pinned = true;
    this.scrollToBottom();
  }
  reply(message) {
    const _this$attrs6 = this.attrs,
      channel = _this$attrs6.channel,
      state = _this$attrs6.state;
    state.setReplyingTo(Number(channel.id()), message);
    m.redraw();
  }
  edit(message) {
    var _message$content;
    const _this$attrs7 = this.attrs,
      channel = _this$attrs7.channel,
      state = _this$attrs7.state;
    state.setEditing(Number(channel.id()), message);
    state.setDraft(Number(channel.id()), (_message$content = message.content()) != null ? _message$content : "");
    m.redraw();
  }
  openThread(message) {
    const thread = message.thread();
    const _this$attrs8 = this.attrs,
      channel = _this$attrs8.channel,
      state = _this$attrs8.state,
      embedded = _this$attrs8.embedded;
    if (thread) {
      state.activeThreadId = Number(thread.id());

      // In the drawer the panel opens over the conversation, the way the pinned
      // list does. Routing here would throw the drawer away and reopen the whole
      // thing full-screen, which is not what clicking a reply count asks for.
      if (embedded) {
        m.redraw();
        return;
      }
      m.route.set(app.route("chat.thread", {
        id: channel.id(),
        threadId: thread.id()
      }));
      return;
    }

    // No thread yet: the send makes one. Staged as a reply like any other, but
    // flagged as a branch — the composer cannot otherwise tell this apart from
    // the plain reply below, and inferring it turned every reply in a
    // threading-enabled channel into a thread.
    state.setReplyingTo(Number(channel.id()), message, null, true);
    m.redraw();
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ChannelView', ChannelView);